import type { MockDatabase } from "@/types/domain";
import type { MockRequest, MockResponse } from "@/mocks/router";
import { extras, recomputeGlobalStats, store } from "@/mocks/store";
import { err, genRippleId, matchPath, nowStamp, ok, paginate, pushAudit } from "./_utils";

export function handleGames(req: MockRequest): MockResponse | null {
  const method = req.method.toUpperCase();
  const path = req.path.replace(/\/+$/, "") || "/";
  const { query, body } = req;

  if (method === "GET" && path === "/api/games") {
    let items = [...store.GAMES];
    const q = query.get("q")?.toLowerCase();
    const status = query.get("status");
    if (q) items = items.filter((g) => g.name.toLowerCase().includes(q) || g.code.toLowerCase().includes(q));
    if (status) items = items.filter((g) => g.status === status);
    return ok(paginate(items, query));
  }
  {
    const m = matchPath("/api/games/:id", path);
    if (m && method === "GET") {
      const game = store.GAMES.find((g) => g.id === m.id);
      if (!game) return err(404, "NOT_FOUND", "游戏不存在");
      return ok({ data: game });
    }
    if (m && method === "PATCH") {
      const idx = store.GAMES.findIndex((g) => g.id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "游戏不存在");
      store.GAMES[idx] = { ...store.GAMES[idx], ...(body as object) };
      recomputeGlobalStats();
      return ok({ data: store.GAMES[idx] });
    }
    if (m && method === "DELETE") {
      const idx = store.GAMES.findIndex((g) => g.id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "游戏不存在");
      const [removed] = store.GAMES.splice(idx, 1);
      store.PACKAGES = store.PACKAGES.filter((p) => p.game_id !== m.id);
      delete extras.secrets[m.id];
      recomputeGlobalStats();
      pushAudit("删除游戏", removed.name, removed.id);
      return ok({ data: removed });
    }
  }
  if (method === "POST" && path === "/api/games") {
    const payload = body as Partial<MockDatabase["GAMES"][number]> & { name?: string };
    const id = payload.id || `g_${Date.now().toString(36)}`;
    const game = {
      id,
      name: payload.name || payload.name_zh || "未命名游戏",
      name_zh: payload.name_zh,
      name_en: payload.name_en,
      code: payload.code || `game_${id}`,
      category: payload.category || "未分类",
      desc: payload.desc || "",
      status: payload.status || "draft",
      icon: payload.icon || "🎮",
      color: payload.color || "from-slate-400 to-slate-600",
      packages: 0,
      today: payload.today || { share: 0, click: 0, install: 0, purchase: 0 },
      convert: payload.convert || 0,
      revenue: payload.revenue || 0,
    };
    store.GAMES.push(game);
    const secret = `gs_live_${Math.random().toString(36).slice(2, 18)}`;
    extras.secrets[id] = secret;
    recomputeGlobalStats();
    pushAudit("创建游戏", game.name, game.id);
    return ok({ data: { game, secret } }, 201);
  }
  {
    const m = matchPath("/api/games/:id/credential", path);
    if (m && method === "GET") {
      const game = store.GAMES.find((g) => g.id === m.id);
      if (!game) return err(404, "NOT_FOUND", "游戏不存在");
      const secret = extras.secrets[m.id] || `gs_live_${m.id.replace(/[^a-z0-9]/gi, "").slice(0, 8)}_demo`;
      return ok({ data: { gameId: game.id, gameSecret: secret, secret } });
    }
  }
  {
    const m = matchPath("/api/games/:id/credential/rotate", path);
    if (m && method === "POST") {
      const game = store.GAMES.find((g) => g.id === m.id);
      if (!game) return err(404, "NOT_FOUND", "游戏不存在");
      const secret = `gs_live_${Math.random().toString(36).slice(2, 18)}`;
      extras.secrets[m.id] = secret;
      pushAudit("轮换密钥", game.name, m.id);
      return ok({ data: { gameId: m.id, gameSecret: secret } });
    }
  }
  {
    const m = matchPath("/api/games/:id/members", path);
    if (m && method === "GET") {
      const members = store.USERS.filter((u) => u.games.includes(m.id) || u.games.includes("all"));
      return ok(paginate(members, query));
    }
    if (m && method === "POST") {
      const payload = body as Partial<MockDatabase["USERS"][number]>;
      const user = {
        id: payload.id || `u_${Date.now().toString(36)}`,
        name: payload.name || "新成员",
        email: payload.email || "",
        role: payload.role || "operator",
        games: payload.games || [m.id],
        lastActive: "刚刚",
        status: payload.status || "active",
      };
      store.USERS.push(user);
      return ok({ data: user }, 201);
    }
  }
  {
    const m = matchPath("/api/games/:id/api-keys", path);
    if (m && method === "GET") {
      return ok({ data: extras.apiKeys[m.id] || [], meta: { total: (extras.apiKeys[m.id] || []).length, page: 1, pageSize: 20 } });
    }
    if (m && method === "POST") {
      const key = {
        id: `key_${Date.now().toString(36)}`,
        name: (body as { name?: string })?.name || "API Key",
        key: `es_${Math.random().toString(36).slice(2, 18)}`,
        scopes: (body as { scopes?: string[] })?.scopes || ["events:write"],
        created_at: nowStamp(),
      };
      extras.apiKeys[m.id] = extras.apiKeys[m.id] || [];
      extras.apiKeys[m.id].push(key);
      return ok({ data: key }, 201);
    }
  }

  return null;
}
